import { useMutation } from '@src/query';


/**
 * Settings → Security mutation hooks (T10). Each wraps the shared `useMutation`
 * engine with the injected-`queryClient` `mutateFn({ apiClient, queryClient })`
 * contract — the corelib query singleton is NOT imported here. Transfer/delete
 * invalidate the org via the `mineKey` the caller already holds from
 * `useMineQuery`, so the exact cache key is preserved unchanged.
 *
 * Mirrors `query/garage/useDonorMutations.js`.
 */

/** Sign out of every session (incl. the current one). Variables: none. No cache
 * invalidation — the caller follows up with a local logout + redirect. */
function useSignOutEverywhereMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient }) => apiClient
      .request({ url: '/users/me/sign-out-everywhere/', method: 'POST' }),
    ...params,
  });
}


/** Transfer org ownership (caller becomes Manager). Variables: `{ sellerId,
 * payload, mineKey }`. */
function useTransferOwnershipMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, payload, mineKey }) => apiClient
      .request({ url: `/sellers/${sellerId}/transfer-ownership/`, method: 'POST', payload })
      .then((data) => queryClient.invalidateQueries({ queryKey: mineKey }).then(() => data)),
    ...params,
  });
}


/** Delete the organization. Variables: `{ sellerId, payload, mineKey }`. */
function useDeleteOrgMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, payload, mineKey }) => apiClient
      .request({ url: `/sellers/${sellerId}/`, method: 'DELETE', payload })
      .then((data) => queryClient.invalidateQueries({ queryKey: mineKey }).then(() => data)),
    ...params,
  });
}


/** Download a zip export of the org's data. Variables: `{ sellerId, fileName }`.
 * Triggers the browser download as a side effect; no cache invalidation. */
function useOrgExportMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, sellerId, fileName }) => apiClient
      .request({ url: `/sellers/${sellerId}/export/`, options: { responseType: 'blob' } })
      .then((data) => {
        const blob = new Blob([data], { type: 'application/zip' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);
      }),
    ...params,
  });
}


export {
  useDeleteOrgMutation,
  useOrgExportMutation,
  useSignOutEverywhereMutation,
  useTransferOwnershipMutation,
};
